if (!customElements.get("push-grid-layout")) {
  class PushGridLayout extends HTMLElement {
    // Initialise les options du composant et les refs runtime.
    constructor() {
      super();
      this.targetSelector = this.getAttribute("target") || "#product-grid";
      this.signature = this.dataset.pushSignature || "";
      this.productsPerPage = parseInt(this.dataset.productsPerPage || "0", 10) || 0;
      this.pageStart = parseInt(this.dataset.pageStart || "1", 10) || 1;
      this.gridObserver = null;
      this.rafId = null;
      this.boundResize = () => this.scheduleLayout();
    }

    // Au montage: invalide le cache si besoin, se connecte a la grille et lance un premier layout.
    connectedCallback() {
      this.invalidateInfiniteScrollCache();
      this.connectToGrid();
      window.addEventListener("resize", this.boundResize);
      this.scheduleLayout();
      // La reserve de fin de flux depend de l'etat de <infinite-scroll>: on relance
      // un layout des que ce composant est defini, sinon on ne sait pas encore
      // s'il reste des pages a charger.
      if (window.customElements) {
        customElements.whenDefined("infinite-scroll").then(() => this.scheduleLayout());
      }
    }

    // Au demontage: nettoie observer, listener resize et raf en cours.
    disconnectedCallback() {
      this.disconnectObserver();
      window.removeEventListener("resize", this.boundResize);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    // Lie le composant a la grille cible et observe les ajouts/retraits d'elements.
    connectToGrid() {
      this.grid = document.querySelector(this.targetSelector);
      this.disconnectObserver();

      if (!this.grid) return;
      this.gridObserver = new MutationObserver(() => this.scheduleLayout());
      this.gridObserver.observe(this.grid, { childList: true });
    }

    // Coupe proprement l'observation MutationObserver.
    disconnectObserver() {
      if (!this.gridObserver) return;
      this.gridObserver.disconnect();
      this.gridObserver = null;
    }

    // Debounce via requestAnimationFrame pour eviter les recalculs trop frequents.
    scheduleLayout() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        if (!this.grid || !document.body.contains(this.grid)) this.connectToGrid();
        this.layoutPushes();
      });
    }

    // Si la signature des pushes change, purge le cache d'infinite scroll en session.
    invalidateInfiniteScrollCache() {
      if (!this.signature) return;
      const key = "PushGridLayout_Signature";
      try {
        const previous = sessionStorage.getItem(key);
        if (previous && previous !== this.signature) {
          Object.keys(sessionStorage).forEach((storageKey) => {
            if (storageKey.indexOf("InfiniteScroll_") === 0) sessionStorage.removeItem(storageKey);
          });
        }
        sessionStorage.setItem(key, this.signature);
      } catch (e) {}
    }

    // Fonction principale: place les pushes puis ajuste la reserve de fin de flux.
    layoutPushes() {
      if (!this.grid) return;
      const cols = window.matchMedia("(max-width: 749px)").matches ? 2 : 4;
      const pushCells = this.placePushes(cols);
      this.applyTrailingReserve(cols, pushCells);
    }

    // Indique s'il reste du contenu a charger: true, false, ou null si <infinite-scroll>
    // n'est pas encore initialise (on conserve alors la decision prise cote serveur).
    hasMoreContent() {
      if (!window.location.pathname.includes("/collections/")) return false;
      const infiniteScroll = document.querySelector("infinite-scroll");
      if (!infiniteScroll) return false;
      if (typeof infiniteScroll.hasMorePages !== "boolean") return null;
      return infiniteScroll.hasMorePages;
    }

    // Retient les derniers produits du flux pour que la derniere ligne visible soit
    // pleine, et les revele des qu'un nouveau paquet arrive ou que le flux est termine.
    applyTrailingReserve(cols, pushCells) {
      const hasMore = this.hasMoreContent();
      if (hasMore === null) return;

      const products = Array.from(this.grid.querySelectorAll(".grid__item:not(.push)"));
      if (!products.length) return;

      const reserve = hasMore ? (products.length + pushCells) % cols : 0;
      const firstReserved = products.length - reserve;

      products.forEach((item, index) => {
        if (reserve > 0 && index >= firstReserved) {
          item.setAttribute("data-reserve", "");
          item.style.display = "none";
        } else if (item.hasAttribute("data-reserve")) {
          item.removeAttribute("data-reserve");
          item.style.display = "";
          // Meme convention que facets.js: un element revele apres coup ne doit pas
          // rester bloque a l'etat initial de l'animation au scroll.
          if (item.classList.contains("scroll-trigger")) item.classList.add("scroll-trigger--cancel");
        }
      });
    }

    // Choisit les pushes a afficher, calcule leur placement CSS grid et renvoie
    // la surface totale (en cellules) des pushes rendus visibles.
    placePushes(cols) {
      const allPushes = Array.from(this.grid.querySelectorAll(".grid__item.push"));
      if (!allPushes.length) return 0;

      // Les produits en reserve sont comptes ici: le placement des pushes ne doit pas
      // dependre de la reserve, sinon les deux calculs oscilleraient l'un contre l'autre.
      const productCount = this.grid.querySelectorAll(".grid__item:not(.push)").length;
      const infiniteScrollEnabled = !!document.querySelector("infinite-scroll");
      const hasAppendedPages =
        infiniteScrollEnabled && this.productsPerPage > 0 && productCount > this.productsPerPage;

      const seenKeys = new Set();
      const uniquePushes = [];

      allPushes.forEach((push, index) => {
        const key = push.dataset.pushKey || `push-${index}`;
        if (seenKeys.has(key)) {
          push.style.display = "none";
          return;
        }
        seenKeys.add(key);
        uniquePushes.push(push);
      });

      const pushData = uniquePushes
        .map((push, index) => {
          const rawWidth = parseInt(push.dataset.pushWidth || "1", 10);
          const rawHeight = parseInt(push.dataset.pushHeight || "1", 10);
          const w = Math.max(1, Math.min(cols, Number.isNaN(rawWidth) ? 1 : rawWidth));
          const h = Math.max(1, Number.isNaN(rawHeight) ? 1 : rawHeight);
          const pos = parseInt(push.dataset.pushPos || "0", 10);
          return { push, index, w, h, pos: Number.isNaN(pos) ? 0 : pos };
        })
        .sort((a, b) => (a.pos === b.pos ? a.index - b.index : a.pos - b.pos));
      const maxSearch = Math.max(500, productCount + pushData.length * cols * 8);

      // Verifie si un bloc w*h peut etre place a partir d'une cellule candidate.
      const canPlaceInGrid = (occupiedSet, candidate, w, h) => {
        const col = ((candidate - 1) % cols) + 1;
        if (col + w - 1 > cols) return null;

        let occupiedBefore = 0;
        occupiedSet.forEach((cell) => {
          if (cell < candidate) occupiedBefore += 1;
        });
        const requiredProductsBefore = candidate - 1 - occupiedBefore;
        if (requiredProductsBefore > productCount) return null;

        const row = Math.floor((candidate - 1) / cols) + 1;
        for (let r = 0; r < h; r += 1) {
          for (let c = 0; c < w; c += 1) {
            const cell = (row + r - 1) * cols + (col + c);
            if (occupiedSet.has(cell)) return null;
          }
        }

        return { row, col };
      };

      // Marque les cellules occupees apres placement d'un push.
      const reserveCells = (occupiedSet, row, col, w, h) => {
        for (let r = 0; r < h; r += 1) {
          for (let c = 0; c < w; c += 1) {
            const cell = (row + r - 1) * cols + (col + c);
            occupiedSet.add(cell);
          }
        }
      };

      const activePushes = [];
      if (hasAppendedPages) {
        // Mode infinite scroll: revele les pushes progressivement
        // selon la surface deja "remplie" (produits + pushes deja actifs).
        let filledCells = productCount;
        pushData.forEach((entry) => {
          const desiredPos = Math.max(1, entry.pos || 1);
          const canReveal = desiredPos <= filledCells;
          if (canReveal) {
            activePushes.push(entry);
            filledCells += entry.w * entry.h;
          } else {
            entry.push.style.display = "none";
          }
        });
      } else {
        pushData.forEach((entry) => {
          if (entry.push.dataset.initialVisible === "true") activePushes.push(entry);
          else entry.push.style.display = "none";
        });
      }

      if (!activePushes.length) return 0;
      activePushes.forEach(({ push }) => {
        push.style.display = "";
      });

      const pushCells = activePushes.reduce((total, entry) => total + entry.w * entry.h, 0);

      const occupied = new Set();

      activePushes.forEach(({ push, w, h, pos }) => {
        // En pagination classique, convertit la position absolue en position locale de page.
        const desired =
          hasAppendedPages
            ? Math.max(1, pos || 1)
            : Math.max(1, (pos || 1) - this.pageStart + 1);
        let row = 1;
        let col = 1;
        let placed = false;

        for (let candidate = desired; candidate <= maxSearch; candidate += 1) {
          const result = canPlaceInGrid(occupied, candidate, w, h);
          if (!result) continue;
          row = result.row;
          col = result.col;
          placed = true;
          break;
        }

        if (!placed) {
          for (let candidate = desired - 1; candidate >= 1; candidate -= 1) {
            const result = canPlaceInGrid(occupied, candidate, w, h);
            if (!result) continue;
            row = result.row;
            col = result.col;
            placed = true;
            break;
          }
        }

        push.style.gridColumn = `${col} / span ${w}`;
        push.style.gridRow = `${row} / span ${h}`;

        reserveCells(occupied, row, col, w, h);
      });

      return pushCells;
    }
  }

  customElements.define("push-grid-layout", PushGridLayout);
}
